/**
 * Sprint 5B — Debiteurendashboard-tegel.
 * Positie: na Omzet-tegel, vóór Tracking-tegel.
 * Data via read-only /admin/reminders/dashboard-metrics.
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  TrendingDown, AlertOctagon, ChevronRight, Loader2, Users,
  Clock, AlertTriangle, CheckCircle2,
} from "lucide-react";
import { adminApi } from "../../../context/AuthContext";

function fmt(x, currency = "EUR") {
  const s = Number(x || 0).toLocaleString("nl-NL", {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
  return `${currency} ${s}`;
}

export default function DebiteurenDashboardCard({ onClick }) {
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await adminApi.get("/admin/reminders/dashboard-metrics");
        setMetrics(r.data);
      } catch {
        setMetrics(null);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleClick = () => {
    if (typeof onClick === "function") {
      onClick();
    } else {
      // Default: navigate naar Facturen sectie
      navigate("/admin?section=facturen&tab=betalingsopvolging");
    }
  };

  return (
    <div
      className="border border-gray-200 bg-white hover:border-red-500 hover:shadow-md cursor-pointer transition-all p-4 group"
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && handleClick()}
      data-testid="dashboard-debiteuren-card"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 flex items-center justify-center bg-[#0A192F] text-white">
            <TrendingDown className="w-4 h-4" />
          </div>
          <div>
            <div className="font-chivo font-bold text-sm text-[#0A192F]">
              Debiteuren
            </div>
            <div className="text-[10px] text-gray-500">
              Openstaande facturen & reminders
            </div>
          </div>
        </div>
        <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-red-500 transition-colors" />
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-gray-500 text-xs">
          <Loader2 className="w-3 h-3 animate-spin" /> Laden...
        </div>
      ) : !metrics ? (
        <div className="text-xs text-gray-500 italic">
          Kan gegevens niet ophalen.
        </div>
      ) : (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2 text-xs">
            <MetricRow
              label="Totaal openstaand"
              value={fmt(metrics.outstanding_total, "EUR")}
              testid="dashboard-debiteuren-total"
              color="text-blue-700"
            />
            <MetricRow
              label="Totaal verlopen"
              value={fmt(metrics.overdue_total, "EUR")}
              testid="dashboard-debiteuren-overdue"
              color={(metrics.overdue_total || 0) > 0 ? "text-red-700" : "text-gray-500"}
              icon={AlertTriangle}
            />
            <MetricRow
              label="Verlopen facturen"
              value={String(metrics.overdue_count || 0)}
              icon={Clock}
              color={(metrics.overdue_count || 0) > 0 ? "text-red-700" : "text-gray-500"}
            />
            <MetricRow
              label="Klanten met saldo"
              value={String(metrics.customer_count_with_outstanding || 0)}
              icon={Users}
              color="text-slate-700"
            />
            <MetricRow
              label="In escalatie"
              value={String(metrics.escalation_count || 0)}
              icon={AlertOctagon}
              color={(metrics.escalation_count || 0) > 0 ? "text-red-700" : "text-gray-500"}
            />
            <MetricRow
              label="Vandaag gepland"
              value={String(metrics.reminders_planned_today || 0)}
              icon={CheckCircle2}
              color="text-emerald-700"
            />
          </div>
          {(metrics.reminders_failed_today || 0) > 0 && (
            <div className="border-l-2 border-red-500 bg-red-50 p-1.5 text-[11px] text-red-800">
              ⚠ {metrics.reminders_failed_today} reminders mislukt vandaag
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function MetricRow({ label, value, icon: Icon, color, testid }) {
  return (
    <div className="flex justify-between items-center border-b border-gray-100 py-0.5"
          data-testid={testid}>
      <span className="text-[10px] uppercase tracking-wide text-gray-500 flex items-center gap-1">
        {Icon ? <Icon className="w-2.5 h-2.5" /> : null}
        {label}
      </span>
      <span className={`font-bold ${color || "text-[#0A192F]"}`}>{value}</span>
    </div>
  );
}
